import React from 'react';
import { University } from '../../../types';

interface TuitionTabProps {
  university: University;
}

const TuitionTab: React.FC<TuitionTabProps> = ({ university }) => {
  // Hardcoded additional costs
  const PERSONAL_EXPENSES = 2000;
  const TRANSPORTATION = 1000;

  // Calculate total cost (tuition + housing + meal plan + books + other costs)
  const calculateTotalCost = () => {
    const tuition = university.out_of_state_tuition || university.in_state_tuition || 0;
    const housing = university.average_housing_cost || 0;
    const mealPlan = university.average_meal_plan_cost || 0;
    const books = university.books_and_supplies || 0;
    const otherCosts = PERSONAL_EXPENSES + TRANSPORTATION;
    return tuition + housing + mealPlan + books + otherCosts;
  };

  // Format currency
  const formatCurrency = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `$${num.toLocaleString()}`;
  };

  // Format percentage from decimal
  const formatPercentage = (decimal: number | null | undefined) => {
    if (!decimal) return 'N/A';
    return `${Math.round(decimal * 100)}%`;
  };

  const totalCost = calculateTotalCost();
  const netPrice = university.net_price_per_year || 0;

  return (
    <div className="tab-content" style={{ width: '100%', maxWidth: 'none', margin: 0, padding: 0 }}>
      <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">Tuition & Costs</h3>
      
      {/* Top Costs Overview */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Net Price Card */}
        <div className="bg-green-50/50 dark:bg-green-900/20 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-green-500 dark:text-green-400 text-2xl mr-3">
              <i className="fas fa-calculator"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Net Price</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">{formatCurrency(netPrice)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">After financial aid</p>
          </div>
        </div>

        {/* Total Cost Card */}
        <div className="bg-blue-50/50 dark:bg-blue-900/20 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-blue-500 dark:text-blue-400 text-2xl mr-3">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Total Cost</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(totalCost)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Per academic year</p>
          </div>
        </div>
      </div>

      {/* Tuition Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="text-purple-500 dark:text-purple-400 text-2xl mr-3">
            <i className="fas fa-graduation-cap"></i>
          </div>
          <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Tuition</h4>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="text-center p-4 bg-purple-50/50 dark:bg-purple-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">In-State</h5>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(university.in_state_tuition)}</p>
          </div>
          <div className="text-center p-4 bg-purple-50/50 dark:bg-purple-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Out-of-State</h5>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(university.out_of_state_tuition)}</p>
          </div>
        </div>
      </div>

      {/* Other Fees Section */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        {/* Housing Card */}
        <div className="bg-gray-100/60 dark:bg-gray-800/60 rounded-xl p-6 flex flex-col items-center justify-center">
          <div className="text-red-500 dark:text-red-400 text-3xl mb-3">
            <i className="fas fa-bed"></i>
          </div>
          <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">Housing</h5>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(university.average_housing_cost)}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Per year</p>
        </div>

        {/* Meal Plan Card */}
        <div className="bg-gray-100/60 dark:bg-gray-800/60 rounded-xl p-6 flex flex-col items-center justify-center">
          <div className="text-yellow-500 dark:text-yellow-400 text-3xl mb-3">
            <i className="fas fa-utensils"></i>
          </div>
          <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">Meal Plan</h5>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(university.average_meal_plan_cost)}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Per year</p>
        </div>

        {/* Books & Supplies Card */}
        <div className="bg-gray-100/60 dark:bg-gray-800/60 rounded-xl p-6 flex flex-col items-center justify-center">
          <div className="text-indigo-500 dark:text-indigo-400 text-3xl mb-3">
            <i className="fas fa-book"></i>
          </div>
          <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">Books & Supplies</h5>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(university.books_and_supplies)}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Per year</p>
        </div>
      </div>

      {/* Financial Aid Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="text-orange-500 dark:text-orange-400 text-2xl mr-3">
            <i className="fas fa-hand-holding-usd"></i>
          </div>
          <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Financial Aid</h4>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="text-center p-4 bg-orange-50/50 dark:bg-orange-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Average Aid Awarded</h5>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(university.average_total_aid_awarded)}</p>
          </div>
          <div className="text-center p-4 bg-orange-50/50 dark:bg-orange-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Students Receiving Aid</h5>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatPercentage(university.students_receiving_financial_aid)}</p>
          </div>
        </div>
      </div>

      {/* Cost Breakdown Summary */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <div className="flex items-start">
          <div className="text-blue-500 dark:text-blue-400 text-xl mr-3 mt-1">
            <i className="fas fa-info-circle"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 dark:text-blue-300 mb-3">Cost Breakdown Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-blue-800 dark:text-blue-300">
              <div>
                <p className="font-semibold mb-2">Direct Costs:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Tuition: {formatCurrency(university.out_of_state_tuition || university.in_state_tuition)}</li>
                  <li>• Housing: {formatCurrency(university.average_housing_cost)}</li>
                  <li>• Meal Plan: {formatCurrency(university.average_meal_plan_cost)}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Indirect Costs:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Books & Supplies: {formatCurrency(university.books_and_supplies)}</li>
                  <li>• Personal Expenses: {formatCurrency(PERSONAL_EXPENSES)}</li>
                  <li>• Transportation: {formatCurrency(TRANSPORTATION)}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TuitionTab;
